import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import styled from '@emotion/styled';
import {
    color_defaults_white,
    color_shades_darkest,
    color_shades_darker,
    color_shades_dark,
    color_shades_light,
    voicify_spark_dark
} from '../../../../../shared/constants/colors';
import { MetricDefinition, getMetricDefinition } from '../../../../../shared/constants/analyticsConstants/metricsDefinitions';

let metricTooltipSequence = 0;

interface MetricInfoTooltipProps {
    /** The metric key from METRICS_DEFINITIONS */
    metricKey: string;
    /** Optional: provide a custom definition instead of looking up by key */
    customDefinition?: MetricDefinition;
    /** Whether to show the calculation explanation */
    showCalculation?: boolean;
    /** Size of the info icon in pixels */
    iconSize?: number;
    /** Custom icon color */
    iconColor?: string;
    /** Additional CSS class */
    className?: string;
    /** Optional full metric trigger instead of the info icon. */
    trigger?: React.ReactElement;
    actionHint?: string;
}

/**
 * Info icon SVG component
 */
const InfoIconSvg: React.FC<{ size: number; color?: string }> = ({ size, color = color_shades_dark }) => (
    <svg
        width={size}
        height={size}
        viewBox="0 0 16 16"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <circle cx="8" cy="8" r="7" stroke={color} strokeWidth="1.5" fill="none" />
        <path
            d="M8 7V11M8 5.5V5"
            stroke={color}
            strokeWidth="1.5"
            strokeLinecap="round"
        />
    </svg>
);

/**
 * MetricInfoTooltip
 * 
 * Displays a small info icon that shows metric definition details on hover.
 * Use this component next to metric labels/titles to provide users with
 * context about how metrics are calculated.
 * 
 * @example
 * <TileTitle>
 *   Contained Conversations <MetricInfoTooltip metricKey="CONTAINED_CALLS_HEALTHCARE" />
 * </TileTitle>
 */
export const MetricInfoTooltip: React.FC<MetricInfoTooltipProps> = ({
    metricKey,
    customDefinition,
    showCalculation = false,
    iconSize = 14,
    iconColor,
    className,
    trigger,
    actionHint
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [isDismissed, setIsDismissed] = useState(false);
    const [descriptionId] = useState(() => `metric-tooltip-description-${++metricTooltipSequence}`);
    const [position, setPosition] = useState<{ x: number; y: number } | null>(null);

    const definition = customDefinition || getMetricDefinition(metricKey);

    if (!definition) {
        console.warn(`MetricInfoTooltip: No definition found for key "${metricKey}"`);
        return null;
    }

    const handleMouseEnter = (e: React.MouseEvent) => {
        setIsDismissed(false);
        setIsHovered(true);
        updatePosition(e.clientX, e.clientY);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        updatePosition(e.clientX, e.clientY);
    };

    const handleMouseLeave = () => {
        setIsHovered(false);
        if (!isFocused) setPosition(null);
    };

    const handleFocus = (e: React.FocusEvent) => {
        const bounds = (e.target as HTMLElement).getBoundingClientRect();
        setIsDismissed(false);
        setIsFocused(true);
        updatePosition(bounds.left, bounds.bottom + 10);
    };

    const handleBlur = (e: React.FocusEvent) => {
        if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget as Node)) return;
        setIsFocused(false);
        if (!isHovered) setPosition(null);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            setIsDismissed(true);
            e.stopPropagation();
        }
    };

    const updatePosition = (clientX: number, clientY: number) => {
        const tooltipWidth = 320;
        const tooltipHeight = 250;
        const padding = 12;

        let x = clientX + padding;
        let y = clientY - 10;

        // Prevent tooltip from going off the right edge
        if (x + tooltipWidth > window.innerWidth) {
            x = clientX - tooltipWidth - padding;
        }

        // Prevent tooltip from going off the bottom edge
        if (y + tooltipHeight > window.innerHeight) {
            y = window.innerHeight - tooltipHeight - 10;
        }

        // Prevent tooltip from going off the top edge
        if (y < 10) {
            y = 10;
        }

        // Prevent tooltip from going off the left edge
        if (x < 10) {
            x = 10;
        }

        setPosition({ x, y });
    };

    const tooltipContent = (isHovered || isFocused) && !isDismissed && position && (
        <TooltipContainer
            role="tooltip"
            style={{
                left: position.x,
                top: position.y
            }}
        >
            {actionHint && <TooltipActionHint>{actionHint}</TooltipActionHint>}
            <TooltipTitle>{definition.name}</TooltipTitle>
            <TooltipDescription>{definition.description}</TooltipDescription>
            {showCalculation && (
                <>
                    <TooltipSectionTitle>How it's calculated:</TooltipSectionTitle>
                    <TooltipCalculation>{definition.calculation}</TooltipCalculation>
                </>
            )}
            {definition.notes && showCalculation && (
                <TooltipNote>
                    <strong>Note:</strong> {definition.notes}
                </TooltipNote>
            )}
        </TooltipContainer>
    );

    return (
        <>
            {trigger ? <MetricTrigger
                onMouseEnter={handleMouseEnter}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
            >{React.cloneElement(trigger, {
                'aria-describedby': [trigger.props['aria-describedby'], descriptionId].filter(Boolean).join(' ')
            })}</MetricTrigger> : <IconWrapper
                className={className}
                tabIndex={0}
                aria-label={definition.name}
                aria-describedby={descriptionId}
                onMouseEnter={handleMouseEnter}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                $iconSize={iconSize}
            >
                <InfoIconSvg size={iconSize} color={iconColor || color_shades_dark} />
            </IconWrapper>}
            <TooltipAccessibleDescription id={descriptionId}>
                {[actionHint, definition.name, definition.description,
                    showCalculation && `How it's calculated: ${definition.calculation}`,
                    showCalculation && definition.notes].filter(Boolean).join(' ')}
            </TooltipAccessibleDescription>
            {tooltipContent && ReactDOM.createPortal(tooltipContent, document.body)}
        </>
    );
};

// Styled Components
const TooltipAccessibleDescription = styled.span`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
`;

const MetricTrigger = styled.span`
    display: block;
`;

const TooltipActionHint = styled.div`
    color: ${voicify_spark_dark};
    font-size: 12px;
    font-weight: 700;
    line-height: 1.4;
    margin-bottom: 10px;
    padding-bottom: 10px;
    border-bottom: 1px solid ${color_shades_light};
`;

const IconWrapper = styled.span<{ $iconSize: number }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: help;
    margin-left: 4px;
    vertical-align: middle;
    opacity: 0.5;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 1;
    }
`;

const TooltipContainer = styled.div`
    position: fixed;
    background: ${color_defaults_white};
    padding: 16px 18px;
    border-radius: 10px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
    max-width: 320px;
    z-index: 99999;
    pointer-events: none;
    border: 1px solid ${color_shades_light};
`;

const TooltipTitle = styled.div`
    font-weight: 700;
    font-size: 14px;
    color: ${color_shades_darkest};
    margin-bottom: 8px;
    letter-spacing: -0.2px;
`;

const TooltipDescription = styled.div`
    font-size: 13px;
    color: ${color_shades_darker};
    line-height: 1.5;
    margin-bottom: 12px;
`;

const TooltipSectionTitle = styled.div`
    font-size: 11px;
    font-weight: 600;
    color: ${color_shades_dark};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 6px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid ${color_shades_light};
`;

const TooltipCalculation = styled.div`
    font-size: 12px;
    color: ${color_shades_darker};
    line-height: 1.5;
`;

const TooltipNote = styled.div`
    font-size: 11px;
    color: ${color_shades_dark};
    line-height: 1.4;
    margin-top: 12px;
    padding: 10px;
    background: rgba(0, 0, 0, 0.03);
    border-radius: 6px;
    
    strong {
        color: ${color_shades_darker};
    }
`;

export default MetricInfoTooltip;
